/** Pure helpers for the Manage Time session screens (no React, no API). */

export const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export type WeekDay = (typeof WEEK_DAYS)[number];

/** 0 = Monday … 6 = Sunday; unknown or blank sorts last. */
export function weekdayIndex(day: string | null | undefined): number {
  const d = String(day ?? "").trim().toLowerCase();
  const i = WEEK_DAYS.findIndex((w) => w.toLowerCase() === d);
  return i === -1 ? WEEK_DAYS.length : i;
}

export function timeToMinutes(t: string | null | undefined): number {
  const m = String(t ?? "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

type DayTimeRow = { day?: string; startTime?: string; dentistName?: string };

/** Monday first, then start time, then dentist name. Returns a new array. */
export function sortByWeekday<T extends DayTimeRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const di = weekdayIndex(a.day) - weekdayIndex(b.day);
    if (di !== 0) return di;
    const am = timeToMinutes(a.startTime);
    const bm = timeToMinutes(b.startTime);
    const tm = (Number.isFinite(am) ? am : 0) - (Number.isFinite(bm) ? bm : 0);
    if (tm !== 0) return tm;
    return String(a.dentistName ?? "").localeCompare(String(b.dentistName ?? ""));
  });
}

export type SessionFormFields = {
  dentistName?: string;
  doctorId?: string;
  day?: string;
  sessionName?: string;
  startTime?: string;
  endTime?: string;
};

const FIELD_LABELS: Array<[keyof SessionFormFields, string]> = [
  ["doctorId", "Dentist"],
  ["day", "Day"],
  ["sessionName", "Session Name"],
  ["startTime", "Start Time"],
  ["endTime", "End Time"],
];

/** Labels of the blank required fields, in form order. */
export function missingSessionFields(row: SessionFormFields): string[] {
  return FIELD_LABELS.filter(([key]) => !String(row[key] ?? "").trim()).map(
    ([, label]) => label
  );
}

/** "Please fill: Dentist, Session Name" or "" when nothing is missing. */
export function missingFieldsMessage(row: SessionFormFields): string {
  const missing = missingSessionFields(row);
  return missing.length ? `Please fill: ${missing.join(", ")}` : "";
}

/** Hint under the session picker for a day's list. */
export function sessionPickerHint(args: {
  day: string;
  count: number;
  source: "day" | "catalogue" | "";
  loading: boolean;
}): string {
  if (args.loading) return "";
  if (args.count === 0) {
    return `No sessions for ${args.day} (create them in General Opening hours)`;
  }
  if (args.source === "catalogue") {
    return `No sessions on ${args.day} yet; showing clinic sessions`;
  }
  return "";
}

/** Confirm text for deleting a general session, naming the cascade. */
export function deleteGeneralSessionConfirm(day: string, sessionName: string): string {
  return `Delete session "${sessionName}" for ${day}? Dentist sessions named "${sessionName}" on ${day} are removed too, and their booked visits lose the room.`;
}

/** One-line result after a general session save or delete that cascaded. */
export function cascadeSummary(args: {
  base: string;
  dentistRows?: number;
  appointments?: number;
  verb: "renamed" | "removed";
}): string {
  const parts: string[] = [];
  const rows = Number(args.dentistRows ?? 0);
  const appts = Number(args.appointments ?? 0);
  if (rows > 0) parts.push(`${rows} dentist slot${rows === 1 ? "" : "s"} ${args.verb}`);
  if (appts > 0) parts.push(`${appts} visit${appts === 1 ? "" : "s"} updated`);
  return parts.length ? `${args.base}. ${parts.join(", ")}` : args.base;
}

/* ---- Multi-day create ---------------------------------------------------- */

export type DaySession = { sessionName: string; startTime: string; endTime: string };

/** Monday to Friday. */
export const WEEKDAYS_ONLY: readonly string[] = WEEK_DAYS.slice(0, 5);

/** Add or remove a day; result stays in week order. */
export function toggleDay(selected: readonly string[], day: string): string[] {
  const set = new Set(selected);
  if (set.has(day)) set.delete(day);
  else set.add(day);
  return Array.from(set).sort((a, b) => weekdayIndex(a) - weekdayIndex(b));
}

/**
 * Sessions available on at least one of `days`, one per name (case-insensitive,
 * first spelling wins), sorted by start. The window is the one common to every
 * day that has the session (latest start, earliest end), because one window is
 * posted to all ticked days and each row must sit inside its own day's session.
 */
export function unionSessions(
  days: readonly string[],
  byDay: Record<string, DaySession[] | undefined>
): DaySession[] {
  const merged = new Map<string, DaySession>();
  for (const day of days) {
    for (const s of byDay[day] ?? []) {
      const name = String(s.sessionName ?? "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const cur = merged.get(key);
      if (!cur) {
        merged.set(key, {
          sessionName: name,
          startTime: s.startTime ?? "",
          endTime: s.endTime ?? "",
        });
        continue;
      }
      const sm = timeToMinutes(s.startTime);
      const em = timeToMinutes(s.endTime);
      const cs = timeToMinutes(cur.startTime);
      const ce = timeToMinutes(cur.endTime);
      if (Number.isFinite(sm) && (!Number.isFinite(cs) || sm > cs)) cur.startTime = s.startTime;
      if (Number.isFinite(em) && (!Number.isFinite(ce) || em < ce)) cur.endTime = s.endTime;
    }
  }
  return Array.from(merged.values()).sort((a, b) => {
    const am = timeToMinutes(a.startTime);
    const bm = timeToMinutes(b.startTime);
    const d = (Number.isFinite(am) ? am : 0) - (Number.isFinite(bm) ? bm : 0);
    return d !== 0 ? d : a.sessionName.localeCompare(b.sessionName);
  });
}

export type MultiDayPlan = {
  /** Days that have a session with that name: these get saved. */
  ok: string[];
  /** Days without it: Save is blocked until they are unticked. */
  missing: string[];
  /** Days whose session list has not loaded yet. */
  unloaded: string[];
};

export function planMultiDaySave(
  days: readonly string[],
  byDay: Record<string, DaySession[] | undefined>,
  sessionName: string
): MultiDayPlan {
  const want = String(sessionName ?? "").trim().toLowerCase();
  const plan: MultiDayPlan = { ok: [], missing: [], unloaded: [] };
  for (const day of days) {
    const list = byDay[day];
    if (!list) {
      plan.unloaded.push(day);
    } else if (list.some((s) => String(s.sessionName ?? "").trim().toLowerCase() === want)) {
      plan.ok.push(day);
    } else {
      plan.missing.push(day);
    }
  }
  return plan;
}

/** "No General session on Saturday, Sunday (untick, or add it in General Opening hours)" or "". */
export function missingDaysMessage(sessionName: string, missing: readonly string[]): string {
  if (!missing.length) return "";
  const name = String(sessionName ?? "").trim() || "that";
  return `No ${name} session on ${missing.join(", ")} (untick, or add it in General Opening hours)`;
}

export type DaySaveResult = { day: string; ok: boolean; message?: string };

/** One success line and one line per failed day. */
export function multiDaySaveSummary(results: readonly DaySaveResult[]): {
  success: string;
  failures: string[];
} {
  const saved = results.filter((r) => r.ok).map((r) => r.day);
  const failures = results
    .filter((r) => !r.ok)
    .map((r) => `${r.day}: ${r.message || "could not save"}`);
  const success = saved.length === 0 ? "" : `Saved ${saved.join(", ")}`;
  return { success, failures };
}
